import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, filter, map, take, timeout } from 'rxjs/operators';
import { AuthenticationService } from '../../service/admin-console/authentication/authentication.service';
import { AuthenticationResponseDto } from '../../service/admin-console/authentication/model/AuthenticationResponseDto';
import { PermissionsService } from '../services/permissions.service';
import { SelectedLanguageService } from '@shared/service/selected-language.service';
import Swal from 'sweetalert2';
import { ADMIN_PORTAL_COMMONS } from '@shared/constants/admin-portal-common.constants';
import { environment } from '@env/environment';
import { APP_ROUTES } from '@core/constants/app-routes.const';

const SESSION_DATA_TIMEOUT_MS = 15000;

export const showPermissionDenied = (
  router: Router,
  selectedLanguageService: SelectedLanguageService
): void => {
  const storedLanguage = selectedLanguageService.getStoredLanguage();
  const currentLang = storedLanguage?.value || 'en';

  const messages: Record<string, string> = {
    en: "You don't have permission to access this feature",
    de: 'Sie haben keine Berechtigung, auf diese Funktion zuzugreifen',
  };

  Swal.fire({
    text: messages[currentLang],
    icon: 'warning',
    confirmButtonColor: ADMIN_PORTAL_COMMONS.THEME_CONFIRM_BUTTON_COLOR,
  });

  router.navigate(['/', APP_ROUTES.USERS]);
};

export const permissionsGuard: CanActivateFn = (
  route,
  state
): Observable<boolean> | boolean => {
  const authService = inject(AuthenticationService);
  const permissionsService = inject(PermissionsService);
  const router = inject(Router);
  const selectedLanguageService = inject(SelectedLanguageService);

  // (temp solution) Check if customer tab is disabled and route starts with AFE_CUSTOMERS
  if (
    !environment.enableCustomerTab &&
    state.url.startsWith(`/${APP_ROUTES.AFE_CUSTOMERS}`)
  ) {
    router.navigate([`/${APP_ROUTES.USERS}`]);
    return false;
  }

  const requiredPrivileges: { test: string[]; prod: string[] } =
    route.data['requiredPrivileges'];

  const evaluate = (sessionData: AuthenticationResponseDto | null): boolean => {
    if (!permissionsService.checkPrivileges(requiredPrivileges)) {
      showPermissionDenied(router, selectedLanguageService);
      return false;
    }

    return true;
  };

  // Fast path: session already loaded in memory
  const currentSession = authService.getSessionData();
  if (currentSession && currentSession.privileges) {
    return evaluate(currentSession);
  }

  // Slow path: wait for the app session to be populated
  if (environment.enableMsalAuth) {
    return authService.sessionData$.pipe(
      filter(
        (data): data is AuthenticationResponseDto => !!data && !!data.privileges
      ),
      take(1),
      timeout(SESSION_DATA_TIMEOUT_MS),
      map(data => evaluate(data)),
      catchError(error => {
        console.error(
          'permissionsGuard: Timeout waiting for session data',
          error
        );

        return of(false);
      })
    );
  }

  return false;
};
